import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, Btn, Input } from "@/components/admin/ui";
import { Download, Filter } from "lucide-react";
import { API } from "@/config";
import { fmtErr } from "@/context/AuthProvider";

const TAG_META = {
  late_delivery: { label: "Late delivery", color: "text-amber-300", ring: "border-amber-500/40 bg-amber-500/5" },
  damaged_item: { label: "Damaged item", color: "text-rose-300", ring: "border-rose-500/40 bg-rose-500/5" },
  item_missing: { label: "Item missing", color: "text-orange-300", ring: "border-orange-500/40 bg-orange-500/5" },
  service_comp: { label: "Customer service comp", color: "text-emerald-300", ring: "border-emerald-500/40 bg-emerald-500/5" },
  duplicate_charge: { label: "Duplicate charge", color: "text-sky-300", ring: "border-sky-500/40 bg-sky-500/5" },
  plans_changed: { label: "Plans changed", color: "text-violet-300", ring: "border-violet-500/40 bg-violet-500/5" },
  other: { label: "Other", color: "text-[#B9C8D5]", ring: "border-[#B9C8D5]/30 bg-black/25" },
  promo_code: { label: "Promo code applied", color: "text-[#FFD166]", ring: "border-[#FFD166]/40 bg-[#FFD166]/5" },
  unspecified: { label: "Unspecified", color: "text-[#B9C8D5]", ring: "border-[#B9C8D5]/30 bg-black/25" },
};

const shortDate = (iso) => (iso ? String(iso).slice(0, 10) : "—");
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
const isoToday = () => new Date().toISOString().slice(0, 10);

export const RefundReport = () => {
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(isoToday());
  const [tag, setTag] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (tag) params.reason_tag = tag;
      const { data } = await axios.get(`${API}/admin/refund-report`, { params });
      setData(data);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Couldn't load report");
    } finally {
      setLoading(false);
    }
  }, [start, end, tag]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.refunds || [];
  const bytag = data?.by_reason || [];

  const exportCsv = () => {
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const header = ["date", "reservation_id", "customer", "email", "amount", "reason_tag", "reason", "by_admin", "code"];
    const csv = [header.join(",")]
      .concat(rows.map((r) => [
        (r.at || "").slice(0, 19),
        r.short_code || r.reservation_id,
        `"${(r.customer_name || "").replace(/"/g, '""')}"`,
        r.customer_email,
        Number(r.amount).toFixed(2),
        r.reason_tag || "",
        `"${(r.reason || "").replace(/"/g, '""')}"`,
        r.by_admin || "",
        r.code || "",
      ].join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refunds_${start}_to_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shortcuts = useMemo(() => ([
    { label: "Last 7 d", start: isoDaysAgo(7) },
    { label: "Last 30 d", start: isoDaysAgo(30) },
    { label: "Last 90 d", start: isoDaysAgo(90) },
    { label: "YTD", start: `${new Date().getFullYear()}-01-01` },
  ]), []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Refund Report</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">Every manual refund + retroactive promo credit — filterable by reason so you can see where the money's actually going.</p>
        </div>
        <Btn onClick={exportCsv} disabled={!rows.length} data-testid="refund-report-export"><Download className="w-3.5 h-3.5" /> Export CSV</Btn>
      </div>

      <Card className="p-4 mb-6">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="eyebrow">From</label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 w-40" data-testid="refund-report-from" />
          </div>
          <div>
            <label className="eyebrow">To</label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 w-40" data-testid="refund-report-to" />
          </div>
          <div>
            <label className="eyebrow flex items-center gap-1"><Filter className="w-3 h-3" /> Reason</label>
            <select value={tag} onChange={(e) => setTag(e.target.value)} className="mt-1 w-52 bg-black/30 border border-[#FFD166]/25 text-[#F6F1E4] px-3 py-2 rounded-lg" data-testid="refund-report-tag">
              <option value="">All reasons</option>
              {Object.entries(TAG_META).map(([id, m]) => id !== "unspecified" ? <option key={id} value={id}>{m.label}</option> : null)}
            </select>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-1.5">
            {shortcuts.map((s) => (
              <button key={s.label} onClick={() => { setStart(s.start); setEnd(isoToday()); }} className="text-[11px] px-2 py-1 rounded-full border border-[#FFD166]/20 text-[#B9C8D5] hover:text-[#FFD166] hover:border-[#FFD166]/50" data-testid={`refund-shortcut-${s.label.toLowerCase().replace(/\s/g, "")}`}>{s.label}</button>
            ))}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]/80">Total refunded</div>
          <div className="font-serif text-3xl text-amber-200 mt-1" data-testid="refund-total">{money(data?.total_refunded)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]/80">Refund events</div>
          <div className="font-serif text-3xl text-[#F6F1E4] mt-1" data-testid="refund-count">{data?.count || 0}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]/80">Avg refund</div>
          <div className="font-serif text-3xl text-[#F6F1E4] mt-1">{data?.count ? money((data.total_refunded || 0) / data.count) : "$0.00"}</div>
        </Card>
      </div>

      {bytag.length > 0 && (
        <Card className="p-4 mb-6">
          <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5]/80 mb-3">By reason</div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {bytag.map((b) => {
              const meta = TAG_META[b.reason_tag] || TAG_META.unspecified;
              return (
                <div key={b.reason_tag} className={`rounded-lg border p-3 ${meta.ring}`} data-testid={`refund-bucket-${b.reason_tag}`}>
                  <div className={`text-[11px] uppercase tracking-widest ${meta.color}`}>{meta.label}</div>
                  <div className="flex items-baseline justify-between mt-1">
                    <div className="font-serif text-2xl text-[#F6F1E4]">{money(b.total)}</div>
                    <div className="text-xs text-[#B9C8D5]">{b.count} event{b.count === 1 ? "" : "s"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="refund-report-table">
            <thead className="bg-black/40 text-[10px] uppercase tracking-widest text-[#B9C8D5]/80">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Reservation</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3">Note</th>
                <th className="text-right px-4 py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-[#B9C8D5] py-10">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-[#B9C8D5] py-10">No refunds in this window.</td></tr>
              ) : rows.map((r, i) => {
                const meta = TAG_META[r.reason_tag] || TAG_META.unspecified;
                return (
                  <tr key={r.id || `${r.reservation_id}-${r.at}`} className="border-t border-[#FFD166]/10">
                    <td className="px-4 py-2 text-xs text-[#B9C8D5]">{shortDate(r.at)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#FFD166]">{r.short_code || r.reservation_id.slice(0, 8)}</td>
                    <td className="px-4 py-2">
                      <div className="text-sm text-[#F6F1E4]">{r.customer_name}</div>
                      <div className="text-[10px] text-[#B9C8D5]">{r.customer_email}</div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.ring} ${meta.color}`}>{meta.label}</span>
                      {r.code && <span className="ml-1 text-[10px] font-mono text-[#FFD166]">{r.code}</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-[#B9C8D5] max-w-md truncate" title={r.reason}>{r.reason || "—"}</td>
                    <td className="px-4 py-2 text-right font-medium text-amber-200">{money(r.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
